<div
  [formGroup]="parentForm()"
  [class.ms-xlarge-b]="displayMargin()"
  (focusout)="closeDropDownOnFocusout($event)"
  [class.enabled]="!parentForm().get(controlName())?.disabled"
>
  <div class="relative ps-xxsmall-b">
    @if (!disableLabel()) {
      <label
        [attr.id]="idOrControlName() + '-label'"
        class="font-body-1 regular-type flex ps-xxsmall-b font-body-medium text-icon-secondary"
        [for]="idOrControlName()"
        [ngClass]="{
          'nordic-blue-400-text': !control().invalid || !control().touched,
          'error-900-text': control().invalid && control().touched,
        }"
      >
        <div class="flex-1">
          {{ label() || (translationPrefix() + '.label' | finLocalization) }}
          @if (showAsterisk()) {
            <span class="flex-1 flex--nogrow" data-testid="asterisk"> *</span>
          }
        </div>
        <ng-template #contentTooltip>
          <h2 class="font-body-1 bold-type nordic-blue-900-text text-icon-primary font-body-medium-bold">
            {{ tooltipHeading() }}
          </h2>
          <p class="pr-xxsmall-t nordic-blue-400-text text-icon-secondary">{{ tooltipContent() }}</p>
        </ng-template>
        @if (tooltipContent()) {
          <button
            type="button"
            fcomPopover
            [options]="tooltipOptions()"
            [content]="contentTooltip"
            [ariaCloseText]="'actions.close' | finLocalization"
            [attr.aria-label]="'actions.info' | finLocalization"
            class="icon nordic-blue-900-text text-icon-primary ms-small-r flex-1 flex--nogrow align-right"
            [class.error-900-text]="control().invalid && control().touched"
            [class.signal-error-notification-border-icon-text]="control().invalid && control().touched"
          >
            <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.HELP_BLOCK" />
          </button>
        }
      </label>
    }

    <div class="relative" #dropDown>
      <div #buttonContainer>
        @if (searchable() === false) {
          <button
            class="input flex flex--middle border rounded-large ps-small-l fill white-bg surface-primary"
            [ngClass]="{
              'is-open': isDropDownOpen(),
              'link-hover': !parentForm().get(controlName())?.disabled,
              'ps-small-r': !!buttonIcon(),
              'flex--space-between': !buttonIcon(),
            }"
            (click)="onInputEnter()"
            (keydown)="handleKeyDown($event)"
            role="combobox"
            [id]="idOrControlName() + '-button'"
            aria-haspopup="listbox"
            tabindex="0"
            [class.btn-disabled]="parentForm().get(controlName())?.disabled === true"
            [attr.aria-activedescendant]="idOrControlName() + '-option-' + activeIndex()"
            [attr.aria-controls]="idOrControlName() + '-options'"
            [attr.aria-expanded]="isDropDownOpen()"
            [attr.aria-labelledby]="idOrControlName() + '-label'"
            [attr.disabled]="parentForm().get(controlName())?.disabled ? 'disabled' : undefined"
            [attr.aria-disabled]="parentForm().get(controlName())?.disabled === true"
            #selectButton
          >
            @if (buttonIcon()) {
              <fcom-icon data-testid="button-icon" class="icon-medium ms-small-r" [name]="buttonIcon()" />
            }
            @if (parentForm().get(controlName())?.disabled === true) {
              <span
                data-testid="disabled-label"
                class="font-body-1 medium-type grey-600-text text-icon-disabled-text font-label-medium"
                >{{ selectedOption() ? selectedOption().name : '-' }}</span
              >
            }
            @if (parentForm().get(controlName())?.disabled === false) {
              @if (!selectedOption()) {
                <span
                  data-testid="select-label"
                  class="font-body-1 medium-type grey-600-text text-icon-disabled-text font-label-medium"
                  >{{ selectLabel() || (selectLabelTranslationKey() | finLocalization) }}</span
                >
              }
              @if (selectedOption()) {
                <span class="font-body-1 medium-type nordic-blue-900-text text-icon-primary font-label-medium">{{
                  selectedOption().name
                    ? selectedOption().name
                    : (translationPrefix() + '.' + selectedOption().value | finLocalization)
                }}</span>
              }
            }
            @if (!buttonIcon()) {
              <fcom-icon
                class="icon-medium ms-small-r ms-xsmall-l"
                [ngClass]="{
                  'grey-600-text text-icon-disabled-text': parentForm().get(controlName())?.disabled === true,
                }"
                [name]="isDropDownOpen() ? SvgLibraryIcon.CHEVRON_UP : SvgLibraryIcon.CHEVRON_DOWN"
              />
            }
          </button>
        }

        @if (searchable()) {
          <div class="text-input relative" [ngClass]="{ 'link-hover': !parentForm().get(controlName())?.disabled }">
            <fcom-text-input
              [id]="idOrControlName()"
              [parentForm]="searchForm"
              [translationPrefix]="translationPrefix()"
              [controlName]="'searchInput'"
              [required]="false"
              [placeHolderText]="searchLabel() || (translationPrefix() + '.searchLabel' | finLocalization)"
              [hideLabel]="true"
              [displayMargin]="false"
              [disableAutoComplete]="true"
              [ariaOptions]="{
                activedescendant: idOrControlName() + '-option-' + activeIndex(),
                controls: idOrControlName() + '-options',
              }"
              [cursorType]="CursorType.POINTER"
              aria-haspopup="listbox"
              role="combobox"
              (click)="openDropDown()"
              (inputEvent)="handleSearch()"
              (keydown)="handleKeyDown($event)"
              (keydown.enter)="onInputEnter()"
              #searchInput
            />
            @if (!isDropDownOpen()) {
              <fcom-icon
                [name]="SvgLibraryIcon.CHEVRON_DOWN"
                class="absolute ms-small-t icon-medium ms-small-r link-hover"
                (click)="openDropDown()"
                data-testid="text-input-chevron"
              />
            }
            @if (isDropDownOpen()) {
              <fcom-icon-button
                class="absolute ms-xxsmall-t ms-xxsmall-r"
                [size]="IconButtonSize.MEDIUM"
                [theme]="IconButtonTheme.DARK_PRIMARY_NORDIC_BLUE"
                [icon]="{ name: SvgLibraryIcon.CLOSE_DELETE_BLOCK }"
                [aria]="{
                  label: clearSearchLabel(),
                }"
                data-testid="clear-search"
                (click)="clearSearch($event)"
              />
            }
          </div>
        }
      </div>
      <ul
        [ngClass]="{ 'is-vishidden': !isDropDownOpen() }"
        class="dropdown white-bg surface-primary absolute rounded-large fill ms-xxsmall-t z1"
        [class.elevation-level-1]="themeService.siteTheme() === FinnairTheme.LEGACY"
        [class.elevation-level-2]="themeService.siteTheme() !== FinnairTheme.LEGACY"
        tabindex="-1"
        role="listbox"
        [attr.data-testid]="id() + '-options'"
        [attr.id]="idOrControlName() + '-options'"
        [attr.aria-labelledby]="idOrControlName() + '-label'"
        #optionsList
      >
        @for (option of visibleOptions(); track option.value; let i = $index) {
          <li
            class="flex link-hover"
            [class.nordic-blue-50-bg]="i === activeIndex()"
            [class.surface-secondary]="i === activeIndex()"
            [class.nordic-blue-50-border]="i === activeIndex()"
            [class.border-primary]="i === activeIndex()"
            role="option"
            [attr.data-testid]="id() + '-option-' + i"
            [attr.id]="idOrControlName() + '-option-' + i"
            [attr.aria-selected]="i === activeIndex()"
            (keydown)="handleKeyDown($event)"
            (click)="selectOption(option)"
          >
            @if (optionTemplate()) {
              <span class="flex flex--column fill">
                <ng-container
                  [ngTemplateOutlet]="optionTemplate()"
                  [ngTemplateOutletContext]="{ name: option.name, value: option.value }"
                />
              </span>
            }
            @if (!optionTemplate()) {
              <span class="flex flex--column ps-small fill">
                {{ option.name ? option.name : (translationPrefix() + '.' + option.value | finLocalization) }}
              </span>
            }
          </li>
        }
      </ul>
    </div>
    <span class="divider"></span>
  </div>
  @if (infoText()) {
    <div class="font-body-2 nordic-blue-400-text text-icon-secondary font-body-small" data-testid="info-text">
      {{ infoText() }}
    </div>
  }
  @if (warningText()) {
    <fcom-notification [theme]="NotificationTheme.WARNING" class="ms-small-t" data-testid="warning-text">{{
      warningText()
    }}</fcom-notification>
  }

  @if (control().invalid && control().touched) {
    <div class="error-label font-body-2 font-body-small" aria-live="polite">
      @if (control().errors?.required) {
        <span class="error-900-text signal-error-notification-border-icon-text">
          {{ translationPrefix() + '.required' | finLocalization }}
        </span>
      }
    </div>
  }
</div>
